import { LoadingOutlined } from '@ant-design/icons'
import { Table as AntdTable } from 'antd'
import { throttle } from 'lodash-es'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import styles from './table.module.scss'
import { normalizeTableSorter, withSortOrder } from './sort'

const InfiniteTable = ({ columns, data, dataSource, hasMore, loadMore, isLoading, onChange, onRowClick, ...otherProps }) => {
    const tableRef = useRef()
    const tableData = dataSource ?? data
    const endElementRef = useRef(null)
    const observer = useRef(null)
    const hasMoreRef = useRef(hasMore)
    const isLoadingRef = useRef(isLoading)
    const loadMoreRef = useRef(loadMore)
    const [searchParams] = useSearchParams()
    const sortBy = searchParams.get('sort[sortBy]')
    const sortType = searchParams.get('sort[sortType]')
    const sortedColumns = useMemo(() => withSortOrder(columns, sortBy, sortType), [columns, sortBy, sortType])

    const debouncedLoadMore = useRef(
        throttle(() => {
            if (hasMoreRef.current && !isLoadingRef.current) loadMoreRef.current?.()
        }, 200)
    ).current

    useEffect(() => {
        hasMoreRef.current = hasMore
        isLoadingRef.current = isLoading
        loadMoreRef.current = loadMore
    }, [hasMore, isLoading, loadMore])

    useEffect(() => {
        return () => {
            debouncedLoadMore.cancel()
        }
    }, [debouncedLoadMore])

    useEffect(() => {
        if (!endElementRef.current || !hasMore) return

        observer.current = new IntersectionObserver((entries) => {
            const anyIntersecting = entries.some((entry) => entry.isIntersecting)

            if (anyIntersecting) {
                debouncedLoadMore()
            }
        })

        observer.current.observe(endElementRef.current)

        return () => {
            observer.current?.disconnect()
            observer.current = null
        }
    }, [hasMore, debouncedLoadMore])

    return (
        <div className={styles.wrapper}>
            <AntdTable
                columns={sortedColumns}
                dataSource={tableData}
                ref={tableRef}
                rowKey="id"
                pagination={false}
                onChange={(pagination, filters, sorter) => {
                    onChange?.(normalizeTableSorter(sorter))
                }}
                onRow={(record, rowIndex) => ({
                    onClick: (event) => {
                        if (event.target.closest('input[type="checkbox"]')) {
                            return
                        }
                        event.preventDefault()
                        onRowClick?.(rowIndex, record)
                    },
                })}
                scroll={{ x: 'max-content' }}
                className="table--fullwidth"
                {...otherProps}
            />
            <div ref={endElementRef} className={styles.scrollToEnd}>
                {tableData?.length > 0 ? (
                    hasMore && isLoading ? <LoadingOutlined /> : 'No more items'
                ) : null}
            </div>
        </div>
    )
}

InfiniteTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    data: PropTypes.arrayOf(PropTypes.object),
    dataSource: PropTypes.arrayOf(PropTypes.object),
    hasMore: PropTypes.bool,
    isLoading: PropTypes.bool,
    loadMore: PropTypes.func,
    onChange: PropTypes.func,
    onRowClick: PropTypes.func,
}

export default InfiniteTable
